export interface LoadTypeRenderConfig {
  textScale: number
  showUnitsInScene: boolean
  showUnitInNewLine: boolean
}

export interface LineLoadRenderConfig extends LoadTypeRenderConfig {
  /** Width (m) of the largest line load value in the 2D floorplan; others scale proportionally. */
  floorplanMaxWidth: number
  /** Smallest width (m) of a non-zero line load end in the 2D floorplan, so small loads stay visible. */
  floorplanMinWidth: number
  /** Line width (px) of the line load outlines and arrows in the 2D floorplan. */
  floorplanLineWidth: number
  /** Height (m) of a line load label line in the 2D floorplan. */
  floorplanTextScale: number
}

export interface PointLoadRenderConfig extends LoadTypeRenderConfig {
  /** Diameter (m) of the circle of the largest point load in the 2D floorplan; others scale. */
  floorplanMaxDiameter: number
  /** Smallest circle diameter (m) of a point load in the 2D floorplan, so small loads stay visible. */
  floorplanMinDiameter: number
  /** Height (m) of a point load label line in the 2D floorplan. */
  floorplanTextScale: number
}

/**
 * Size of the inner forces envelope in the 3D scene: each member's envelope is scaled so that its
 * largest value is drawn with this ordinate.
 */
export interface InnerForcesEnvelopeRenderConfig {
  /** Largest ordinate (m), shared by moments and shear forces. */
  maxOrdinate: number
}

export interface LoadRenderConfig {
  lineLoads: LineLoadRenderConfig
  pointLoads: PointLoadRenderConfig
  innerForcesEnvelope: InnerForcesEnvelopeRenderConfig
}

export const defaultLoadRenderConfig: LoadRenderConfig = {
  lineLoads: {
    textScale: 0.07,
    showUnitsInScene: true,
    showUnitInNewLine: false,
    floorplanMaxWidth: 0.4,
    floorplanMinWidth: 0.06,
    floorplanLineWidth: 2,
    floorplanTextScale: 0.05,
  },
  pointLoads: {
    textScale: 0.07,
    showUnitsInScene: true,
    showUnitInNewLine: true,
    floorplanMaxDiameter: 0.8,
    floorplanMinDiameter: 0.05,
    floorplanTextScale: 0.05,
  },
  innerForcesEnvelope: {
    maxOrdinate: 3,
  },
}

export const mergeLoadRenderConfig = (
  current: LoadRenderConfig,
  persisted?: Partial<LoadRenderConfig>,
): LoadRenderConfig => {
  // the fixed point load diameter was replaced by `floorplanMaxDiameter`; the old value is dropped
  const { floorplanDiameter: _floorplanDiameter, ...persistedPointLoads } =
    (persisted?.pointLoads as Partial<PointLoadRenderConfig> & { floorplanDiameter?: number }) ?? {}

  return {
    lineLoads: {
      ...current.lineLoads,
      ...persisted?.lineLoads,
    },
    pointLoads: {
      ...current.pointLoads,
      ...persistedPointLoads,
    },
    innerForcesEnvelope: {
      ...current.innerForcesEnvelope,
      ...persisted?.innerForcesEnvelope,
    },
  }
}
